import { Box, Checkbox, MenuItem, Typography } from '@material-ui/core';
import { LenderMap, Pricing } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';

import { usePricingEditor } from '../hooks/usePricingEditor';
import { LenderEarningFee } from '../types/LenderEarningFee';
import { PricingValueObject } from '../types/PricingValueObject';

interface RemoveAllPricingInLenderContextFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  pricing: Pricing;
}

export const RemoveAllPricingInLenderContextForm = ({
  open,
  setOpen,
  pricing,
}: RemoveAllPricingInLenderContextFormProps) => {
  const { removePricingInLenderContexts } = usePricingEditor(pricing.id);

  const [selectedEarningFees, setSelectedEarningFees] = useState<LenderEarningFee[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const pricingVO = new PricingValueObject(pricing);
  const lenderEarningFees = pricingVO.uniqueLenderEarningFees();

  useEffect(() => {
    return () => {
      setSelectedEarningFees([]);
      setIsSubmitting(false);
    };
  }, []);

  const disableSubmit = !!(selectedEarningFees && selectedEarningFees.length === 0);

  const onSubmit = async (): Promise<void> => {
    if (selectedEarningFees === null) return;
    setIsSubmitting(true);
    await removePricingInLenderContexts(selectedEarningFees);
    setIsSubmitting(false);
    setOpen(false);
  };

  if (selectedEarningFees === null) return <></>;

  return (
    <NemuruDialog
      opened={open}
      setOpened={setOpen}
      buttons={
        <>
          <StandardButton main onClick={onSubmit} disabled={disableSubmit} loading={isSubmitting}>
            {disableSubmit ? (
              <Text tId="REMOVE_PRICING_SPECS" />
            ) : (
              <Text tId="REMOVE_X_PRICING_SPECS" dynamicDataTexts={[selectedEarningFees.length]} />
            )}
          </StandardButton>
        </>
      }
    >
      <Box mb={5}>
        <Font variant="h3" color="title">
          <Text tId={'REMOVE_PRICING_SPEC_IN_LENDER_CONTEXT'} />
        </Font>
      </Box>
      <Box mb={4}>
        <Font variant="body2" color="textSecondary">
          <Text tId={'REMOVE_PRICING_SPEC_IN_LENDER_CONTEXT_DESCRIPTION'} />
        </Font>
      </Box>

      <Box>
        {lenderEarningFees.map((earningFee) => {
          const checked = selectedEarningFees.includes(earningFee);
          const onClick = () => {
            let earningFees = [...selectedEarningFees, earningFee];
            if (checked)
              earningFees = [...selectedEarningFees].filter((selectedEarningFee) => selectedEarningFee !== earningFee);
            setSelectedEarningFees(earningFees);
          };
          return (
            <MenuItem
              key={earningFee.lenderId}
              onClick={onClick}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Box display={'flex'}>
                <LenderIcon
                  lenderName={LenderMap.fromLenderId(earningFee.lenderId)}
                  style={{
                    maxHeight: 24,
                    maxWidth: 24,
                    margin: '0px 16px 0px 0px',
                    filter: checked ? 'none' : 'grayscale(100%) brightness(65%) contrast(150%)',
                    opacity: checked ? 1 : 0.5,
                  }}
                />
                <Typography color={checked ? 'textPrimary' : 'textSecondary'}>
                  {earningFee.pricingNameInLenderContext}
                </Typography>
              </Box>
              <Box display={'flex'} alignItems={'center'}>
                <Typography color={checked ? 'textPrimary' : 'textSecondary'} variant={'body2'}>
                  <Text tId={'VERSION'} />
                  {': '}
                  {`${earningFee.pricingVersion}`}
                </Typography>
                <Checkbox checked={checked} />
              </Box>
            </MenuItem>
          );
        })}
      </Box>
    </NemuruDialog>
  );
};
